import { Survey } from '../models/survey';
import { LocalStorageService } from '../services/local-storage.service';
import { StorageManagerService } from '../services/storage-manager.service';

/**
 * Try to load a stored survey.
 * Falls back to legacy localStorage records when the StorageManagerService API is missing.
 */
export async function getSurveyFromStorageWithFallback(
  storageManagerService: StorageManagerService,
  localStorageService: LocalStorageService,
  surveyId: string
): Promise<Survey | null> {
  const maybeGetter = (storageManagerService as any)?.getSurveyData;

  if (typeof maybeGetter === 'function') {
    try {
      return await maybeGetter.call(storageManagerService, surveyId);
    } catch (error) {
      console.warn(`Primary storage retrieval failed for ${surveyId}:`, error);
    }
  } else {
    console.warn('storageManagerService.getSurveyData is not available, using legacy storage fallback');
  }

  return await legacySurveyLookup(localStorageService, surveyId);
}

/**
 * Legacy lookup that hits localStorage (or its async wrapper) directly.
 */
async function legacySurveyLookup(
  localStorageService: LocalStorageService,
  surveyId: string
): Promise<Survey | null> {
  const keyBase = `construct-${surveyId}`;
  const candidateKeys = [keyBase, `${keyBase}_compressed`];

  for (const key of candidateKeys) {
    try {
      const legacyValue = await localStorageService.getAsync<any>(key);
      const normalized = normalizeStoredSurvey(legacyValue);
      if (normalized) {
        console.warn(`Recovered survey from legacy storage key ${key}`);
        return normalized;
      }
    } catch (error) {
      console.warn(`Legacy storage read failed for key ${key}:`, error);
    }
  }

  // Last attempt: direct access to localStorage (covers private browsing or migration issues)
  try {
    const directValue = localStorage.getItem(keyBase);
    const normalized = normalizeStoredSurvey(directValue);
    if (normalized) {
      console.warn(`Recovered survey from direct localStorage key ${keyBase}`);
      return normalized;
    }
  } catch (error) {
    console.warn('Direct localStorage access failed:', error);
  }

  return null;
}

/**
 * Normalize any persisted survey shape to the Survey interface.
 */
export function normalizeStoredSurvey(value: any): Survey | null {
  if (!value) {
    return null;
  }

  // Handle Dexie StorageRecord shape
  if (isStorageRecord(value)) {
    return normalizeStoredSurvey(value.value);
  }

  // Strings may contain serialized surveys or compressed payloads
  if (typeof value === 'string') {
    try {
      return normalizeStoredSurvey(JSON.parse(value));
    } catch (error) {
      console.warn('Failed to parse stored survey string:', error);
      return null;
    }
  }

  // Handle compressed payloads used by StorageManagerService
  if (value && value._compressed && typeof value.data === 'string') {
    try {
      return JSON.parse(value.data) as Survey;
    } catch (error) {
      console.warn('Failed to decompress survey payload:', error);
      return null;
    }
  }

  return value as Survey;
}

function isStorageRecord(candidate: any): candidate is { value: any } {
  if (!candidate || typeof candidate !== 'object') {
    return false;
  }

  const maybeRecord = candidate as Record<string, any>;
  const hasStorageKeys = 'value' in maybeRecord && ('key' in maybeRecord || 'createdAt' in maybeRecord);
  return hasStorageKeys;
}
